import { getSuburbPostcode, toLocationSlug } from './stateCore.mjs';
import type { StateSlug } from './stateCore.mjs';

/**
 * L5 (operator-ruled 2026-08-20) — does the platform have a built directory page for
 * this suburb?
 *
 * ⚠️ EXTRACTED RATHER THAN INLINED IN THE PAGE, and the reason is a measured test
 * weakness rather than taste: while this lived inline, three mutations of its body
 * (`return true`, `return false`, and dropping the normalise step) ALL SURVIVED the
 * guard. The guard asserted the ORACLE (map ↔ routes 1:1) and the WIRING (which signal
 * the empty state reads) and could not see the logic between them. **A branch you
 * cannot call is a branch you cannot test.**
 *
 * WHAT IT ANSWERS, precisely: "did the build make a directory page for this suburb" —
 * which is exactly the question the two empty states disagree about. NOT "is this a
 * real place" and NOT "does it have gyms".
 *
 * ⭐ WHY THE POSTCODE MAP IS A LEGITIMATE ORACLE, measured 2026-08-20 and asserted by
 * `directoryCoverage.test.ts` so it cannot rot silently: the map's entries and the
 * built location routes are an EXACT 1:1 — 988 and 988, nothing on either side alone,
 * and zero routes whose address disagrees with the map. It already ships in the client
 * bundle because it addresses every URL the app builds, so this costs no payload and
 * behaves identically on a cold load and a client navigation — which is the whole point,
 * since the bug it replaces only ever appeared on client navigation.
 *
 * ⚠️ THE NORMALISE STEP IS PART OF THE CONTRACT, not incidental: callers hold a DISPLAY
 * string ("Parramatta 2150", "Blacktown NSW 2148"), and the map is keyed by bare slug.
 * Passing the display string straight in returns '' for every suburb on the site.
 */
export function hasDirectoryFor(suburbDisplay: string, state: StateSlug): boolean {
    // ⚠️ EQUIVALENT MUTANT, documented so nobody re-runs it: deleting this guard changes
    // no result today, because toLocationSlug('') returns the literal 'nsw' (a
    // DEFAULT_STATE fallback inside the normaliser) and no state's map has a 'nsw' key,
    // so an empty input already yields false. It stays because that is a COINCIDENCE of
    // the fallback's value, not a property anyone guaranteed — and relying on it would
    // make this function's correctness depend on a quirk two modules away.
    if (!suburbDisplay) return false;
    return getSuburbPostcode(toLocationSlug(suburbDisplay), state) !== '';
}
